"use client";

import { useEffect, useState } from "react";
import { DriveFile, DrivePickedFile, IUserDrive } from "../models/drive";
import { useUser } from "../context/UserContext";
import { authorizeGoogleDrive } from "../helpers/firebase";
import { AlertType } from "../constants/enums";
import { iDiveUser } from "../models/interfaceResponse";

const DRIVE_BASE = "https://www.googleapis.com/drive/v3";
const GOOGLE_DOC = "application/vnd.google-apps.document";
const WORD_MIME_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];


export const WORD_TYPES = [
  "application/vnd.google-apps.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

const DRIVE_TOKEN_TTL = 60 * 60 * 1000; // 1 hora
const DRIVE_TOKEN_SAFETY = 5 * 60 * 1000; // renovar 5 min antes

export function useGoogleDrive() {
  const { setUserDrive, addAlert } = useUser();
  const [pickerReady, setPickerReady] = useState(false);

  useEffect(() => {
    if (!window.gapi) return;
    window.gapi.load("picker", { callback: () => setPickerReady(true) });
  }, []);
  // verificar token 
  const isTokenNearExpiry = (user: any): boolean => {
    if (!user?.issuedAt) return true;
    const elapsed = Date.now() - user.issuedAt;
    return elapsed >= DRIVE_TOKEN_TTL - DRIVE_TOKEN_SAFETY;
  };
 
 const getAccessToken = async (user: IUserDrive): Promise<string> => {
    if (!user?.accessToken) {
      const userDriveData = await authorizeGoogleDrive();
      setUserDrive(userDriveData);
      return userDriveData.accessToken;
    } 
    const healthyData = await driveHealthCheck(user);
    if (!healthyData.success || !healthyData.data?.accessToken) {
      throw new Error("No se pudo obtener accessToken de Google Drive");
    } 
    return healthyData.data.accessToken;
  };
 
  const driveHealthCheck = async (user: IUserDrive): Promise<iDiveUser<IUserDrive>> => { 
    try {
      
      if (!user?.accessToken) return { code:200, message:"token no encontrado", success:false, data: user };
      if (!isTokenNearExpiry(user)) { return {code:200, message:"", success:true, data: user }; } 
      const res = await fetch( `${DRIVE_BASE}/about?fields=user`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      );
      if (res.ok) { return {code:200, message:"", success:true, data: user }; }
      if (res.status === 401 || res.status === 403) {
        const userDriveData = await authorizeGoogleDrive();
        setUserDrive(userDriveData);
         return {code:200, message:"", success:true, data: userDriveData }; 
      } 
      return {code:200, message:"Health Check no response", success:false, data:{} as IUserDrive}; 
    } catch (e) {
      console.error("Drive healthcheck error", e);
      let statusCode = 500;
      let message = "Error inesperado validando Google Drive";
      if (e instanceof Error) { message = e.message; }
      if (typeof e === "object" && e !== null) {
        const err = e as any;
        if (typeof err.status === "number") statusCode = err.status;
        if (typeof err.code === "number") statusCode = err.code;
      }
      addAlert(message, AlertType.Warning);
      return { code: statusCode, message, success: false, data: e as any, };
    }
  };

  const openPicker = async (user:IUserDrive): Promise<DrivePickedFile | null> => {
    if (!pickerReady || !window.google?.picker) {
      throw new Error("Google Picker no está listo");
    }     
    const accessToken = await getAccessToken(user);
    return new Promise((resolve) => {
      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes(
          [
            "application/pdf",
            "application/vnd.ms-excel",
            "application/vnd.google-apps.spreadsheet",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/msword",
            "application/vnd.google-apps.document",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ].join(",")
        ); 
      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(process.env.NEXT_PUBLIC_GOOGLE_API_KEY)
        .setCallback((data: any) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs[0];
            resolve({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
            });
          }
          if (data.action === window.google.picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      picker.setVisible(true);
    });
  };

  const getFileMetadata = async (user:IUserDrive,fileId: string): Promise<DriveFile> => {
    const accessToken = await getAccessToken(user);    
    const res = await fetch(
      `${DRIVE_BASE}/files/${fileId}?fields=id,name,mimeType,version,modifiedTime,webViewLink`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    ); 
    if (!res.ok) {
      const error: any = new Error("No se pudo obtener metadata");
      error.status = res.status;
      throw error;
    } 
    return res.json();
  };

  const downloadFile = async (user:IUserDrive, fileId: string): Promise<Blob> => {
    const accessToken =await getAccessToken(user);
    const res = await fetch(
      `${DRIVE_BASE}/files/${fileId}?alt=media`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    ); 
    if (!res.ok) {
      const error: any = new Error("Error descargando archivo");
      error.status = res.status;
      throw error;
    }
    return res.blob();
  };

  const downloadFileDrive = async (user:IUserDrive, fileId: string, mimeType: string): Promise<Blob> => {
    if (mimeType === GOOGLE_DOC) {
      return exportDocToPdf(user, fileId);
    }

    if (WORD_MIME_TYPES.includes(mimeType)) {
      return  await convertWordToPdf(user, fileId);
    }
    // OTROS ARCHIVOS EXCEL , PDF
    return downloadFile(user, fileId) 
    
  };

   const exportDocToPdf = async ( user: IUserDrive, fileId: string ): Promise<Blob> => {
    const accessToken = await getAccessToken(user);

    const res = await fetch(
      `${DRIVE_BASE}/files/${fileId}/export?mimeType=application/pdf`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!res.ok) {
      const error: any = new Error("Error exportando Google Doc a PDF");
      error.status = res.status;
      throw error;
    }

    return res.blob();
  };

  const convertWordToPdf = async ( user: IUserDrive, fileId: string ): Promise<Blob> => {
    const accessToken = await getAccessToken(user);
    let tempGoogleDocId: string | null = null;
    try {
      // convertimos a doogle doc
      const copyRes = await fetch(
        `${DRIVE_BASE}/files/${fileId}/copy`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mimeType: "application/vnd.google-apps.document",
          }),
        }
      );

      if (!copyRes.ok) {
        throw new Error(`Error copiando documento (${copyRes.status})`);
      }

      const { id } = await copyRes.json();
      tempGoogleDocId = id;
      // convertir a pdf
      const exportRes = await fetch(
        `${DRIVE_BASE}/files/${tempGoogleDocId}/export?mimeType=application/pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/pdf",
          },
        }
      );

      if (!exportRes.ok) {
        throw new Error(`Error exportando PDF (${exportRes.status})`);
      }

      return await exportRes.blob();
    } finally {
      // eliminados doc temporal
      if (tempGoogleDocId) {
        try {
          await fetch(`${DRIVE_BASE}/files/${tempGoogleDocId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
        } catch {
          // cleanup defensivo
        }
      }
    }
  };



  return {
    openPicker,
    getFileMetadata,
    downloadFile,
    downloadFileDrive
  };
}
